import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EventOfflineControls } from './EventOfflineControls';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { ScrollArea } from '@/components/ui/scroll-area';
import { Music, Play, SkipForward, ThumbsUp, Check, X, QrCode, RefreshCw, Users, Clock, TrendingUp, Flame, Volume2, ChevronLeft, ChevronRight, Smartphone, Monitor, History, AlertTriangle, GripVertical, BarChart3 } from 'lucide-react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, TouchSensor } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LiveQR } from './LiveQR';
import { LiveEscaleta } from './LiveEscaleta';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config';
import { resolveSpotifyUrl } from '../utils/spotifyUrl';
import { openExternalLink } from '../utils/openExternalLink';
import { ExternalLink } from 'lucide-react';
import { MomentoSubTabs } from './MomentoSubTabs';
import { guessBucketFromPeticion, BUCKET_LABELS } from '../utils/momentoBuckets';

const API = API_URL;

// ============================================
// SWIPEABLE REQUEST CARD (Mobile)
// ============================================
const SwipeableRequestCard = ({ request, onApprove, onReject, onMarkPlayed, isPlaying, alreadyPlayed }) => {
  const x = useMotionValue(0);
  const background = useTransform(
    x,
    [-150, 0, 150],
    ['rgba(239, 68, 68, 0.3)', 'transparent', 'rgba(34, 197, 94, 0.3)']
  );
  const leftOpacity = useTransform(x, [-150, -50, 0], [1, 0.5, 0]);
  const rightOpacity = useTransform(x, [0, 50, 150], [0, 0.5, 1]);

  const handleDragEnd = (event, info) => {
    const threshold = 100;
    if (info.offset.x > threshold) {
      onApprove();
    } else if (info.offset.x < -threshold) {
      onReject();
    }
  };

  return (
    <motion.div 
      className="relative mb-3 overflow-hidden rounded-xl"
      style={{ background }}
    >
      {/* Background indicators */}
      <motion.div 
        className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 text-red-500"
        style={{ opacity: leftOpacity }}
      >
        <X className="w-8 h-8" />
        <span className="font-bold">RECHAZAR</span>
      </motion.div>
      <motion.div 
        className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 text-green-500"
        style={{ opacity: rightOpacity }}
      >
        <span className="font-bold">APROBAR</span>
        <Check className="w-8 h-8" />
      </motion.div>

      {/* Draggable card */}
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.7}
        onDragEnd={handleDragEnd}
        style={{ x }}
        className={`relative z-10 bg-slate-800 border ${isPlaying ? 'border-emerald-500 shadow-emerald-500/20 shadow-lg' : 'border-slate-700'} rounded-xl p-4 cursor-grab active:cursor-grabbing`}
        whileTap={{ scale: 0.98 }}
      >
        <div className="flex items-center gap-3">
          {/* Album art placeholder / Music icon */}
          <div className={`w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 ${isPlaying ? 'bg-emerald-600' : 'bg-slate-700'}`}>
            {isPlaying ? (
              <Volume2 className="w-7 h-7 text-white animate-pulse" />
            ) : (
              <Music className="w-7 h-7 text-slate-400" />
            )}
          </div>

          {/* Song info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-white font-semibold truncate">{request.titulo}</h4>
              {alreadyPlayed && (
                <Badge className="bg-amber-600/20 text-amber-400 border-amber-600/30 text-xs">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Repetida
                </Badge>
              )}
            </div>
            <p className="text-slate-400 text-sm truncate">{request.artista}</p>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-slate-500">Por: {request.nombre_cliente || 'Invitado'}</span>
              <div className="flex items-center gap-1 text-emerald-400">
                <ThumbsUp className="w-3 h-3" />
                <span className="text-xs font-bold">{request.votos || 0}</span>
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex flex-col gap-1">
            {!request.reproducida && !request.rechazada && (
              <Button
                onClick={(e) => { e.stopPropagation(); onMarkPlayed(); }}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 h-8 px-2"
              >
                <Play className="w-4 h-4" />
              </Button>
            )}
            {/* Feb-2026: botón Spotify SIEMPRE presente (link directo o búsqueda) */}
            {(() => {
              const sp = resolveSpotifyUrl(request);
              if (!sp) return null;
              return (
                <Button
                  onClick={(e) => { e.stopPropagation(); openExternalLink(sp.url); }}
                  size="sm"
                  className={`h-8 px-2 text-white ${sp.isSearch ? 'bg-emerald-800 hover:bg-emerald-700' : 'bg-green-600 hover:bg-green-700'}`}
                  title={sp.isSearch ? 'Buscar en Spotify' : 'Abrir en Spotify'}
                  data-testid={`livemode2-swipe-spotify-${request.id}`}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              );
            })()}
          </div>
        </div>

        {/* Swipe hint */}
        <div className="mt-2 flex justify-center">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <ChevronLeft className="w-4 h-4 text-red-400" />
            <span>Desliza</span>
            <ChevronRight className="w-4 h-4 text-green-400" />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================
// SORTABLE QUEUE ITEM (Drag & Drop)
// ============================================
const SortableQueueItem = ({ request, onMarkPlayed, position }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: request.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border ${isDragging ? 'border-cyan-500 bg-slate-700' : 'border-slate-700 bg-slate-800'}`}
    >
      <div 
        {...attributes} 
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-700 rounded"
      >
        <GripVertical className="w-5 h-5 text-slate-500" />
      </div>
      
      <div className="w-8 h-8 rounded-full bg-cyan-600 flex items-center justify-center text-white font-bold text-sm">
        {position}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{request.titulo}</p>
        <p className="text-slate-400 text-sm truncate">{request.artista}</p>
      </div>
      
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 text-emerald-400">
          <ThumbsUp className="w-4 h-4" />
          <span className="font-bold">{request.votos || 0}</span>
        </div>
        {/* Feb-2026: botón Spotify SIEMPRE (link directo o búsqueda) */}
        {(() => {
          const sp = resolveSpotifyUrl(request);
          if (!sp) return null;
          return (
            <Button
              onClick={() => openExternalLink(sp.url)}
              size="sm"
              className={`text-white ${sp.isSearch ? 'bg-emerald-800 hover:bg-emerald-700' : 'bg-green-600 hover:bg-green-700'}`}
              title={sp.isSearch ? 'Buscar en Spotify' : 'Abrir en Spotify'}
              data-testid={`livemode2-queue-spotify-${request.id}`}
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          );
        })()}
        <Button
          onClick={onMarkPlayed}
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Play className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// STATS CARD
// ============================================
const StatsCard = ({ icon: Icon, label, value, color = 'cyan', trend }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-${color}-400 text-xs uppercase tracking-wide`}>{label}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {trend !== undefined && (
            <p className={`text-xs mt-1 ${trend > 0 ? 'text-green-400' : trend < 0 ? 'text-red-400' : 'text-slate-500'}`}>
              {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'} {Math.abs(trend)} últimos 10min
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl bg-${color}-600/20`}>
          <Icon className={`w-6 h-6 text-${color}-400`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ============================================
// ACTIVITY CHART (Simple bar chart)
// ============================================
const ActivityChart = ({ data }) => {
  const maxValue = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="flex items-end gap-1 h-20">
      {data.map((item, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full bg-cyan-600 rounded-t transition-all duration-300"
            style={{ height: `${(item.count / maxValue) * 100}%`, minHeight: item.count > 0 ? '4px' : '0' }}
          />
          <span className="text-xs text-slate-500">{item.label}</span>
        </div>
      ))}
    </div>
  );
};

// ============================================
// MAIN COMPONENT: LiveMode2
// ============================================
export const LiveMode2 = ({
  currentPlaylist,
  token,
  songs = [],
  onAddToCustomList,
  activeBucket = 'baile',
  onActiveBucketChange,
  momentoOverrides = {},
}) => {
  const [requests, setRequests] = useState([]);
  const [approvedQueue, setApprovedQueue] = useState([]);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [playedHistory, setPlayedHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('mobile'); // 'mobile' | 'desktop'
  const [showQR, setShowQR] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [hotMoment, setHotMoment] = useState(false);
  const [activityData, setActivityData] = useState([]);
  const [connectedGuests, setConnectedGuests] = useState(0);
  
  const previousRequestsCount = useRef(0);
  const audioRef = useRef(null);

  const eventId = currentPlaylist?.id;
  const eventCode = currentPlaylist?.client_access_code || currentPlaylist?.access_code;
  const portalUrl = eventCode ? `${window.location.origin}/live/${eventCode}` : null;

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  // Stats calculation — filtradas por bucket de momento activo para coherencia
  // con la pestaña "Peticiones" y con el LiveDashboard. Las peticiones vivas de
  // invitados que no tienen `momento` asignado caen a Baile por defecto vía
  // `guessBucketFromPeticion` (mismo helper usado en el pad).
  const filteredByBucket = React.useMemo(
    () => requests.filter((r) => guessBucketFromPeticion(r) === activeBucket),
    [requests, activeBucket]
  );
  const stats = {
    total: filteredByBucket.length,
    pending: filteredByBucket.filter(r => r.estado === 'pendiente' && !r.rechazada && !r.reproducida).length,
    approved: filteredByBucket.filter(r => r.estado === 'aprobada' && !r.reproducida).length,
    played: filteredByBucket.filter(r => r.reproducida).length,
    rejected: filteredByBucket.filter(r => r.rechazada).length,
    totalVotes: filteredByBucket.reduce((sum, r) => sum + (r.votos || 0), 0),
  };

  // Fetch requests
  const fetchRequests = useCallback(async (showLoader = true) => {
    if (!eventId) return;
    
    if (showLoader) setLoading(true);
    
    try {
      const response = await axios.get(`${API}/eventos/${eventId}/peticiones/live?estado=todas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allRequests = response.data.peticiones || [];
      
      // Check for new requests (sound notification)
      const pendingCount = allRequests.filter(r => r.estado === 'pendiente' && !r.rechazada).length;
      if (pendingCount > previousRequestsCount.current && previousRequestsCount.current > 0 && soundEnabled) {
        playNotificationSound();
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
      }
      previousRequestsCount.current = pendingCount;
      
      setRequests(allRequests);

      // Update approved queue (only approved, not played, filtrado por bucket)
      const approved = allRequests
        .filter(r => r.estado === 'aprobada' && !r.reproducida)
        .filter(r => guessBucketFromPeticion(r) === activeBucket);
      setApprovedQueue(approved);

      // Update played history (filtrado por bucket)
      const played = allRequests
        .filter(r => r.reproducida)
        .filter(r => guessBucketFromPeticion(r) === activeBucket)
        .sort((a, b) =>
        new Date(b.fecha_reproducida || b.fecha_peticion) - new Date(a.fecha_reproducida || a.fecha_peticion)
      );
      setPlayedHistory(played);
      
      // Detect hot moment (many pending requests)
      setHotMoment(pendingCount >= 5);
      
      // Generate activity data (last 6 intervals)
      generateActivityData(allRequests);
      
      // Estimate connected guests (unique voters in last 30 min)
      const thirtyMinAgo = Date.now() - 30 * 60 * 1000;
      const recentVoters = new Set();
      allRequests.forEach(r => {
        if (new Date(r.fecha_peticion).getTime() > thirtyMinAgo) {
          (r.votantes || []).forEach(v => recentVoters.add(v));
        }
      });
      setConnectedGuests(recentVoters.size);
      
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId, token, soundEnabled, activeBucket]);

  // Generate activity chart data
  const generateActivityData = (allRequests) => {
    const now = new Date();
    const intervals = [];
    
    for (let i = 5; i >= 0; i--) {
      const start = new Date(now.getTime() - (i + 1) * 10 * 60 * 1000);
      const end = new Date(now.getTime() - i * 10 * 60 * 1000);
      
      const count = allRequests.filter(r => {
        const date = new Date(r.fecha_peticion);
        return date >= start && date < end;
      }).length;
      
      intervals.push({
        label: i === 0 ? 'Ahora' : `-${(i) * 10}m`,
        count
      });
    }
    
    setActivityData(intervals);
  };

  // Play notification sound
  const playNotificationSound = () => {
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
    } catch (e) {
      console.warn('Error playing notification sound:', e);
    }
  };

  // Handle moderation
  const handleModeration = async (requestId, action) => {
    try {
      await axios.patch(`${API}/peticiones/${requestId}/moderar`, 
        { tipo: action, motivo: action === 'rechazar' ? 'No disponible' : '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const messages = {
        aprobar: '✓ Petición aprobada',
        rechazar: '✗ Petición rechazada',
        marcar_reproducida: '🎵 Marcada como reproducida'
      };
      
      toast.success(messages[action] || 'Acción completada');
      fetchRequests(false);
    } catch (error) {
      console.error('Moderation error:', error);
      toast.error('Error al moderar');
    }
  };

  // Set now playing
  const handleSetNowPlaying = (request) => {
    setNowPlaying(request);
    handleModeration(request.id, 'marcar_reproducida');
  };

  // Check if song was already played
  const checkAlreadyPlayed = (request) => {
    return playedHistory.some(p => 
      p.titulo?.toLowerCase() === request.titulo?.toLowerCase() &&
      p.artista?.toLowerCase() === request.artista?.toLowerCase() &&
      p.id !== request.id
    );
  };

  // Handle drag end for queue reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setApprovedQueue((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Add to custom list
  const handleAddToCustomList = (request) => {
    if (onAddToCustomList) {
      // Find song in library by title/artist
      const matchingSong = songs.find(s => 
        s.title?.toLowerCase().includes(request.titulo?.toLowerCase()) ||
        s.artist?.toLowerCase().includes(request.artista?.toLowerCase())
      );
      
      if (matchingSong) {
        onAddToCustomList(matchingSong.id);
        toast.success('Canción añadida a la lista del evento');
      } else {
        toast.info('Canción no encontrada en tu biblioteca');
      }
    }
  };

  // Auto-refresh
  useEffect(() => {
    fetchRequests();
    const interval = setInterval(() => fetchRequests(false), 5000);
    return () => clearInterval(interval);
  }, [fetchRequests]);

  // Pending requests for swipe view (filtrado por bucket)
  const pendingRequests = filteredByBucket
    .filter(r => r.estado === 'pendiente' && !r.rechazada && !r.reproducida)
    .sort((a, b) => (b.votos || 0) - (a.votos || 0));

  if (!currentPlaylist) {
    return (
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-12 text-center">
          <Music className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">Selecciona un evento para gestionar peticiones en vivo</p>
        </CardContent>
      </Card>
    );
  }

  if (!eventCode) {
    return (
      <div className="space-y-4" data-testid="live-no-code">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-8 text-center">
            <QrCode className="w-14 h-14 text-amber-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-white mb-2">Peticiones de invitados desactivadas</h3>
            <p className="text-slate-400 text-sm">
              Genera un código de acceso desde "Portal Cliente" para habilitar las peticiones en vivo de los invitados. La escaleta del DJ ya está disponible abajo.
            </p>
          </CardContent>
        </Card>

        {/* La escaleta del DJ NO requiere código de invitados */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <LiveEscaleta playlist={currentPlaylist} token={token} />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Hidden audio for notifications */}
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleN5f0zCsLPCz/fCa6uL+DQAAAA==" />
      
      {/* Header with mode toggle and hot moment indicator */}
      <Card className={`border-slate-700 ${hotMoment ? 'bg-gradient-to-r from-orange-900/30 to-red-900/30 border-orange-600/50' : 'bg-slate-800'}`}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl ${hotMoment ? 'bg-orange-600' : 'bg-cyan-600'}`}>
                {hotMoment ? <Flame className="w-6 h-6 text-white animate-pulse" /> : <Music className="w-6 h-6 text-white" />}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  Modo Live 2.0
                  {hotMoment && <Badge className="bg-orange-600 animate-pulse">🔥 Momento Caliente</Badge>}
                </h2>
                <p className="text-slate-400 text-sm">{currentPlaylist.nombre_novios || 'Evento'}</p>
                <div className="mt-2">
                  <EventOfflineControls
                    playlistId={currentPlaylist.id}
                    eventDate={currentPlaylist.fecha_evento}
                    peticiones={currentPlaylist.peticiones}
                  />
                </div>
                {/* Subpestañas por momento — coherentes con la pestaña "Peticiones".
                    Las peticiones de invitados sin `momento` caen a Baile. */}
                {typeof onActiveBucketChange === 'function' && (
                  <div className="mt-3 pt-3 border-t border-slate-700/50">
                    <MomentoSubTabs
                      peticiones={requests}
                      activeBucket={activeBucket}
                      onChange={onActiveBucketChange}
                      overrides={momentoOverrides}
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Sound toggle */}
              <Button
                onClick={() => setSoundEnabled(!soundEnabled)}
                variant="outline"
                size="sm"
                className={soundEnabled ? 'bg-cyan-600/20 border-cyan-600' : 'bg-slate-700'}
              >
                <Volume2 className={`w-4 h-4 ${soundEnabled ? 'text-cyan-400' : 'text-slate-500'}`} />
              </Button>
              
              {/* View mode toggle */}
              <div className="flex border border-slate-600 rounded-lg overflow-hidden">
                <Button
                  onClick={() => setViewMode('mobile')}
                  variant="ghost"
                  size="sm"
                  className={`rounded-none ${viewMode === 'mobile' ? 'bg-cyan-600 text-white' : ''}`}
                >
                  <Smartphone className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => setViewMode('desktop')}
                  variant="ghost"
                  size="sm"
                  className={`rounded-none ${viewMode === 'desktop' ? 'bg-cyan-600 text-white' : ''}`}
                >
                  <Monitor className="w-4 h-4" />
                </Button>
              </div>
              
              {/* QR button */}
              <Button onClick={() => setShowQR(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <QrCode className="w-4 h-4 mr-2" />
                QR
              </Button>
              
              {/* Refresh */}
              <Button onClick={() => fetchRequests(false)} variant="outline" size="sm">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatsCard icon={Clock} label="Pendientes" value={stats.pending} color="amber" />
        <StatsCard icon={ThumbsUp} label="Votos" value={stats.totalVotes} color="emerald" />
        <StatsCard icon={Users} label="Invitados" value={connectedGuests} color="cyan" />
        <StatsCard icon={Play} label="Reproducidas" value={stats.played} color="purple" />
      </div>

      {/* Activity Chart */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Actividad (última hora)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ActivityChart data={activityData} />
        </CardContent>
      </Card>

      {/* Now Playing Card */}
      {nowPlaying && (
        <Card className="bg-gradient-to-r from-emerald-900/30 to-cyan-900/30 border-emerald-600/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-emerald-600 flex items-center justify-center">
                <Volume2 className="w-8 h-8 text-white animate-pulse" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-emerald-400 uppercase tracking-wide">Sonando Ahora</p>
                <h3 className="text-xl font-bold text-white">{nowPlaying.titulo}</h3>
                <p className="text-slate-400">{nowPlaying.artista}</p>
              </div>
              <Button
                onClick={() => setNowPlaying(null)}
                variant="ghost"
                size="sm"
                className="text-slate-400"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Area */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 bg-slate-800">
          <TabsTrigger value="pending" className="data-[state=active]:bg-amber-600">
            Pendientes ({stats.pending})
          </TabsTrigger>
          <TabsTrigger value="queue" className="data-[state=active]:bg-cyan-600">
            Cola ({stats.approved})
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-purple-600">
            Historial ({stats.played})
          </TabsTrigger>
          <TabsTrigger value="top" className="data-[state=active]:bg-emerald-600">
            Top 5
          </TabsTrigger>
          <TabsTrigger value="escaleta" className="data-[state=active]:bg-sky-600" data-testid="live-tab-escaleta">
            Escaleta
          </TabsTrigger>
        </TabsList>

        {/* Pending Tab - Swipe Cards (Mobile) or List (Desktop) */}
        <TabsContent value="pending" className="mt-4">
          {pendingRequests.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-12 text-center">
                <Music className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No hay peticiones pendientes</p>
                <p className="text-slate-500 text-sm mt-2">Las peticiones de los invitados aparecerán aquí</p>
              </CardContent>
            </Card>
          ) : viewMode === 'mobile' ? (
            <div className="space-y-1">
              <p className="text-xs text-slate-500 text-center mb-3">
                👆 Desliza derecha para aprobar, izquierda para rechazar
              </p>
              <AnimatePresence>
                {pendingRequests.map((request) => (
                  <SwipeableRequestCard
                    key={request.id}
                    request={request}
                    onApprove={() => handleModeration(request.id, 'aprobar')}
                    onReject={() => handleModeration(request.id, 'rechazar')}
                    onMarkPlayed={() => handleSetNowPlaying(request)}
                    isPlaying={nowPlaying?.id === request.id}
                    alreadyPlayed={checkAlreadyPlayed(request)}
                  />
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="space-y-2">
              {pendingRequests.map((request) => (
                <Card key={request.id} className="bg-slate-800 border-slate-700">
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
                          <Music className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-medium text-sm sm:text-base truncate">{request.titulo}</h4>
                            {checkAlreadyPlayed(request) && (
                              <Badge className="bg-amber-600/20 text-amber-400 text-xs flex-shrink-0">Repetida</Badge>
                            )}
                          </div>
                          <p className="text-slate-400 text-xs sm:text-sm truncate">{request.artista}</p>
                          <p className="text-slate-500 text-xs truncate">Por: {request.nombre_cliente}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                        <div className="flex items-center gap-1 text-emerald-400">
                          <ThumbsUp className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="font-bold text-sm">{request.votos || 0}</span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            onClick={() => handleModeration(request.id, 'aprobar')}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 h-8 w-8 sm:h-9 sm:w-9 p-0"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleSetNowPlaying(request)}
                            size="sm"
                            className="bg-cyan-600 hover:bg-cyan-700 h-8 w-8 sm:h-9 sm:w-9 p-0"
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                          {/* Feb-2026: botón Spotify SIEMPRE (directo o búsqueda) */}
                          {(() => {
                            const sp = resolveSpotifyUrl(request);
                            if (!sp) return null;
                            return (
                              <Button
                                onClick={() => openExternalLink(sp.url)}
                                size="sm"
                                className={`h-8 w-8 sm:h-9 sm:w-9 p-0 text-white ${sp.isSearch ? 'bg-emerald-800 hover:bg-emerald-700' : 'bg-green-600 hover:bg-green-700'}`}
                                title={sp.isSearch ? 'Buscar en Spotify' : 'Abrir en Spotify'}
                                data-testid={`livemode2-pending-spotify-${request.id}`}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            );
                          })()}
                          <Button
                            onClick={() => handleModeration(request.id, 'rechazar')}
                            size="sm"
                            variant="destructive"
                            className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Queue Tab - Drag & Drop Reordering */}
        <TabsContent value="queue" className="mt-4">
          {approvedQueue.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-12 text-center">
                <SkipForward className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No hay canciones en cola</p>
                <p className="text-slate-500 text-sm mt-2">Aprueba peticiones para añadirlas aquí</p>
              </CardContent>
            </Card>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={approvedQueue.map(r => r.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 mb-2">
                    ⬆️⬇️ Arrastra para reordenar la cola
                  </p>
                  {approvedQueue.map((request, index) => (
                    <SortableQueueItem
                      key={request.id}
                      request={request}
                      position={index + 1}
                      onMarkPlayed={() => handleSetNowPlaying(request)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          {playedHistory.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-12 text-center">
                <History className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No hay canciones reproducidas aún</p>
              </CardContent>
            </Card>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-2 pr-4">
                {playedHistory.map((request, index) => (
                  <Card key={request.id} className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-purple-600/20 flex items-center justify-center text-purple-400 text-sm font-bold">
                          {playedHistory.length - index}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{request.titulo}</p>
                          <p className="text-slate-400 text-sm truncate">{request.artista}</p>
                        </div>
                        <div className="flex items-center gap-1 text-slate-500 text-sm">
                          <ThumbsUp className="w-3 h-3" />
                          {request.votos || 0}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </TabsContent>

        {/* Top 5 Tab */}
        <TabsContent value="top" className="mt-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                Top 5 Más Votadas — {BUCKET_LABELS[activeBucket] || activeBucket}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredByBucket.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No hay peticiones aún en {BUCKET_LABELS[activeBucket] || activeBucket}</p>
              ) : (
                <div className="space-y-3">
                  {[...filteredByBucket]
                    .sort((a, b) => (b.votos || 0) - (a.votos || 0))
                    .slice(0, 5)
                    .map((request, index) => (
                      <div key={request.id} className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg ${
                          index === 0 ? 'bg-amber-500 text-black' :
                          index === 1 ? 'bg-slate-400 text-black' :
                          index === 2 ? 'bg-orange-700 text-white' :
                          'bg-slate-700 text-slate-300'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{request.titulo}</p>
                          <p className="text-slate-400 text-sm truncate">{request.artista}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <ThumbsUp className="w-5 h-5 text-emerald-400" />
                          <span className="text-xl font-bold text-emerald-400">{request.votos || 0}</span>
                        </div>
                        {!request.reproducida && !request.rechazada && (
                          <Button
                            onClick={() => handleSetNowPlaying(request)}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))
                  }
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Escaleta Tab - checklist en vivo */}
        <TabsContent value="escaleta" className="mt-4" data-testid="live-tab-content-escaleta">
          <LiveEscaleta playlist={currentPlaylist} token={token} />
        </TabsContent>
      </Tabs>

      {/* QR Dialog */}
      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-center">Portal de Invitados</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4">
            <LiveQR url={portalUrl} size={250} caption="Escanea para pedir canciones" />
            <code className="bg-slate-700 px-4 py-2 rounded text-sm break-all">{portalUrl}</code>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(portalUrl);
                toast.success('URL copiada');
              }}
              className="w-full bg-cyan-600 hover:bg-cyan-700"
            >
              Copiar URL
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveMode2;
